import { apiClient } from 'src/api/apiClient';
import { GET_TEMPLATE_DETAILS } from 'src/constants/httpEndPoints';
import { featuredCrmIntegration } from 'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/utils/playbook-utils';

type CommonHeaders = { emailId: string; uniqueId: string };

// Map template names to dynamic option keys we need to fetch from API
const TEMPLATE_TO_DYNAMIC_KEY: Record<string, 'intentTopics' | 'relevantTechnologies'> = {
  'Intent Signals Targeting': 'intentTopics',
  'Tech Signals': 'relevantTechnologies',
  'Identifying TechStack for most website visited prospect': 'relevantTechnologies',
};

// Map dynamic keys to the child filter name inside appliedFilters.filterData
const DYNAMIC_KEY_TO_FILTER_NAME: Record<'intentTopics' | 'relevantTechnologies', string> = {
  intentTopics: 'Intent Topics',
  relevantTechnologies: 'Technology',
};

const getDynamicKeyForTemplate = (
  templateName?: string,
): 'intentTopics' | 'relevantTechnologies' | null => {
  if (!templateName) return null;
  return TEMPLATE_TO_DYNAMIC_KEY[templateName] || null;
};

const fetchOptionsForDynamicKey = async (
  key: 'intentTopics' | 'relevantTechnologies',
  headers: CommonHeaders,
): Promise<any[]> => {
  try {
    // console.log('key0000-before', key);
    const res = await apiClient.post(
      GET_TEMPLATE_DETAILS,
      {
        customerId: headers.uniqueId,
        relevantTechnologies: true,
        intentTopics: true,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-customer-id': headers.uniqueId,
          'x-login-email': headers.emailId,
        },
      },
    );
    // console.log('res0000', res.data, key);
    return Array.isArray(res.data?.[key]) ? (res.data[key] as any[]) : [];
  } catch (err) {
    console.warn('Failed to fetch dynamic options for key:', key, err);
    return [];
  }
};

// Inject fetched options into includes for either:
// 1) a direct match on entry.filterName === filterName with root-level includes/excludes in filterData
// 2) a nested child present at entry.filterData[filterName] with includes/excludes inside the child
const injectOptionsIntoCriteriaNode = (
  nodes: Array<Record<string, any>>,
  filterName: string,
  options: any[],
  nodeType = 'criteriaAccountNode',
): Array<Record<string, any>> => {
  if (!Array.isArray(nodes) || !filterName || options.length === 0) return nodes;

  // Only criteriaAccountNode as per current requirement
  const idx = nodes.findIndex((n) => n && n.type === nodeType);
  if (idx < 0) return nodes;

  const node = nodes[idx];
  const clonedNodes = [...nodes];
  const clonedNode = { ...node, data: { ...(node.data || {}) } } as any;

  const filtersObj = clonedNode.data?.filters;
  if (!filtersObj || typeof filtersObj !== 'object') return nodes;
  const appliedFilters = filtersObj.appliedFilters;
  if (!appliedFilters || typeof appliedFilters !== 'object') return nodes;

  let updated = false;
  const nextApplied: Record<string, any> = {};
  Object.entries(appliedFilters).forEach(([key, value]) => {
    const entry = value as any;
    if (entry && entry.filterData) {
      const container = { ...(entry.filterData as any) };

      // Case 1: Non-nested shape where filterName equals entry.filterName and includes/excludes at root
      if (entry?.filterName === filterName) {
        const includes: any[] = Array.isArray((container as any).includes)
          ? (container as any).includes
          : [];
        const set = new Set<any>(includes);
        options.forEach((opt) => {
          if (!set.has(opt)) set.add(opt);
        });
        const merged = Array.from(set);
        entry.filterData = {
          ...(container || {}),
          includes: merged,
          excludes: Array.isArray((container as any)?.excludes) ? (container as any).excludes : [],
        };
        updated = true;
      }

      // Case 2: Nested shape where child exists at filterData[filterName]
      else if (
        container &&
        typeof container === 'object' &&
        Object.prototype.hasOwnProperty.call(container, filterName)
      ) {
        const child = { ...(container as any)[filterName] } as any;
        const includes: any[] = Array.isArray(child.includes) ? child.includes : [];
        const set = new Set<any>(includes);
        options.forEach((opt) => {
          if (!set.has(opt)) set.add(opt);
        });
        const merged = Array.from(set);
        (container as any)[filterName] = {
          ...child,
          includes: merged,
          excludes: Array.isArray(child?.excludes) ? child.excludes : [],
        };
        entry.filterData = container;
        updated = true;
      }
    }
    nextApplied[key] = entry;
  });

  if (!updated) return nodes;

  clonedNode.data = {
    ...clonedNode.data,
    filters: {
      ...filtersObj,
      appliedFilters: nextApplied,
    },
  };
  clonedNodes[idx] = clonedNode;
  return clonedNodes;
};

export const applyDynamicTemplateOptionsToNodes = async (
  template: { name?: string },
  nodes: Array<Record<string, any>>,
  headers: CommonHeaders,
): Promise<Array<Record<string, any>>> => {
  if (template?.name === 'Contacts of specific criteria from CRM to CDP') {
    const crmSources = Object.values(featuredCrmIntegration) as Array<string>;
    return injectOptionsIntoCriteriaNode(nodes, 'Source', crmSources, 'criteriaContactNode');
  }
  const dynamicKey = getDynamicKeyForTemplate(template?.name);
  if (!dynamicKey) return nodes;
  const filterName = DYNAMIC_KEY_TO_FILTER_NAME[dynamicKey];
  const options = await fetchOptionsForDynamicKey(dynamicKey, headers);
  if (!options || options.length === 0) return nodes;
  return injectOptionsIntoCriteriaNode(nodes, filterName, options);
};

export const __dynamicTemplateOptionsTesting = {
  getDynamicKeyForTemplate,
  fetchOptionsForDynamicKey,
  injectOptionsIntoCriteriaNode,
};

// ---------------------
// Generic injection for appliedFilters into criteriaAccountNode
// Allows future reuse beyond custom AI signals
// ---------------------

type InjectAppliedFilterConfig = {
  categoryKey: string; // e.g., 'AI Insights'
  childFilterName: string; // e.g., 'Custom Signal 3'
  includes?: any[]; // e.g., ['Direct']
  excludes?: any[];
};

export const injectAppliedFilterIntoCriteriaNode = (
  nodes: Array<Record<string, any>>,
  config: InjectAppliedFilterConfig,
): Array<Record<string, any>> => {
  if (!Array.isArray(nodes)) return nodes;
  const { categoryKey, childFilterName } = config;
  if (!categoryKey || !childFilterName) return nodes;

  const idx = nodes.findIndex((n) => n && n.type === 'criteriaAccountNode');
  if (idx < 0) return nodes;

  const node = nodes[idx];
  const clonedNodes = [...nodes];
  const clonedNode = { ...node, data: { ...(node.data || {}) } } as any;

  const nextFilters = { ...(clonedNode.data?.filters || {}) };
  const nextApplied = { ...(nextFilters.appliedFilters || {}) } as Record<string, any>;
  const existingCategory = nextApplied[categoryKey] || {};
  const nextFilterData = { ...(existingCategory.filterData || {}) };

  const includes = Array.isArray(config.includes) ? config.includes : ['Direct'];
  const excludes = Array.isArray(config.excludes) ? config.excludes : [];

  nextFilterData[childFilterName] = {
    includes,
    excludes,
  };

  nextApplied[categoryKey] = {
    filterKey: categoryKey,
    filterName: categoryKey,
    filterData: nextFilterData,
  };

  clonedNode.data = {
    ...clonedNode.data,
    filters: {
      ...nextFilters,
      appliedFilters: nextApplied,
      filterCriteria: nextFilters.filterCriteria || { filters: [] },
    },
  };
  clonedNodes[idx] = clonedNode;
  return clonedNodes;
};

// Convenience helper for current requirement: apply selected Custom AI Signal
export const applyCustomAiSignalToNodes = (
  nodes: Array<Record<string, any>>,
  customSignalName: string,
): Array<Record<string, any>> => {
  return injectAppliedFilterIntoCriteriaNode(nodes, {
    categoryKey: 'AI Insights',
    childFilterName: customSignalName,
    includes: ['Direct'],
  });
};

// Remove entire category (e.g., 'AI Insights') from criteriaAccountNode appliedFilters
export const removeCategoryFromCriteriaNode = (
  nodes: Array<Record<string, any>>,
  categoryKey: string,
): Array<Record<string, any>> => {
  if (!Array.isArray(nodes)) return nodes;
  const idx = nodes.findIndex((n) => n && n.type === 'criteriaAccountNode');
  if (idx < 0) return nodes;
  const node = nodes[idx];
  const clonedNodes = [...nodes];
  const clonedNode = { ...node, data: { ...(node.data || {}) } } as any;
  const filtersObj = clonedNode.data?.filters;
  if (!filtersObj || typeof filtersObj !== 'object') return nodes;
  const applied = { ...(filtersObj.appliedFilters || {}) } as Record<string, any>;
  if (!Object.prototype.hasOwnProperty.call(applied, categoryKey)) return nodes;
  delete applied[categoryKey];
  clonedNode.data = {
    ...clonedNode.data,
    filters: {
      ...filtersObj,
      appliedFilters: applied,
    },
  };
  clonedNodes[idx] = clonedNode;
  return clonedNodes;
};

// Apply Custom AI Signal with support for single-select (replace any previous children under category)
export const applyCustomAiSignalToNodesWithMode = (
  nodes: Array<Record<string, any>>,
  customSignalName: string,
  multi: boolean,
): Array<Record<string, any>> => {
  if (multi) return applyCustomAiSignalToNodes(nodes, customSignalName);
  // Single-select: replace category with only the selected child
  if (!Array.isArray(nodes)) return nodes;
  const idx = nodes.findIndex((n) => n && n.type === 'criteriaAccountNode');
  if (idx < 0) return nodes;
  const node = nodes[idx];
  const clonedNodes = [...nodes];
  const clonedNode = { ...node, data: { ...(node.data || {}) } } as any;
  const nextFilters = { ...(clonedNode.data?.filters || {}) };
  const nextApplied = { ...(nextFilters.appliedFilters || {}) } as Record<string, any>;
  nextApplied['AI Insights'] = {
    filterKey: 'AI Insights',
    filterName: 'AI Insights',
    filterData: {
      [customSignalName]: {
        includes: ['Direct'],
        excludes: [],
      },
    },
  };
  clonedNode.data = {
    ...clonedNode.data,
    filters: {
      ...nextFilters,
      appliedFilters: nextApplied,
      filterCriteria: nextFilters.filterCriteria || { filters: [] },
    },
  };
  clonedNodes[idx] = clonedNode;
  return clonedNodes;
};
